import { Injectable } from '@angular/core'
import { TranslateService, TranslatePipe, LangChangeEvent } from '@ngx-translate/core';

let Shepherd = require('tether-shepherd');

export class GuidedTour extends Shepherd.Tour{

}

/**
 * @author banerjees
 * @see guided-tour.scss
 */
@Injectable()
export class GuidedTourBuilder {

    private tours = [];

    constructor() {
    }

    /**
     * Can be invoked from the constructor of the calling component.
     * @param tourId
     * @param onStart
     * @param onCancel
     * @param onComplete
     */
    createTour(tourId: string, onStart=()=>{}, onCancel=()=>{}, onComplete=()=>{}) : any {

        //----- [ Overwrite an existing tour if any ] -----//
        let existingIfAny:number = this.tours.reduce(
                (matchedIndex:number, tour, index) => tour.id === tourId ? index : matchedIndex, -1);
        if(existingIfAny != -1) {
            this.tours.splice(existingIfAny);
        }

        let tour = new Shepherd.Tour({
            defaults: {
              //@see guided-tour.scss
              classes: 'guided-tour',

              // That looks ugly with 'true'
              showCancelLink: true,
              id: tourId
            }
          });

        tour.on('start', onStart);
        tour.on('cancel', onCancel);
        tour.on('complete', onComplete);
        this.tours.push({id: tourId, tour: tour});

        console.debug('Created Guided-Tour: ', tourId);
        return tour;
    }

    /**
     * Methods that can be invoked on a tour instance:
     * <ul>
     * <li> next(): Advance to the next step, in the order they were added </li>
     * <li> back(): Show the previous step, in the order they were added </li>
     * <li> cancel(): Trigger cancel on the current step, hiding it without advancing </li>
     * <li> hide(): Hide the current step </li>
     * <li> show([id]): Show the step specified by id (if it's a string), or index (if it's a number) provided. Defaults to the first step. </li>
     * <li> start(): Show the first step and begin the tour </li>
     * <li> getCurrentStep(): Returns the currently shown step </li>
     * <li> on(eventName, handler, [context]): Bind an event </li>
     * <li> off(eventName, [handler]): Unbind an event </li>
     * <li> once(eventName, handler, [context]): Bind just the next instance of an event </li>
     * </ul>
     * @param tourId
     */
    findTour(tourId: string) {
        let entry = this.tours.find( tour => tour.id === tourId );

        if(!entry) {
            console.debug("Could not find tour with Id: ", tourId);
            return undefined;
        }
        return entry.tour;
    }

    listTourIds(): string[] {
        return this.tours.map(tour => tour.id);
    }

    /**
     * Should be invoked from the 'ngAfterViewInit' or '' of the calling component.
     * @param tourId
     * @param step of type (example)
     * <pre>
     * {
            title: 'Step 1',
            text: 'This is Sample - Step 1',
            attachTo: {element:'#kkk', on: 'right'},
            classes: 'shepherd-theme-arrows',
            //advanceOn: '.side-menu click',
            buttons: [
                {
                  text: 'Exit',
                  classes: 'shepherd-button-secondary',
                  action: () => {console.warn("Cancelling Tour!!!", tour); tour.hide(); tour.cancel()}
                }, {
                  text: 'Next',
                  action: () => {console.warn("Going FWD!!!", tour);  tour.hide(); tour.next()},
                  classes: 'shepherd-button-example-primary'
                }
              ]
          }
        </pre>
     */
    addStep(tourId: string, stepId: string, step: any) {
        let tour = this.findTour(tourId);

        if(!tour) {
            console.warn("[AddStep] No GuidedTour found with Id:", tourId);
        }
        else {
            // -- [Check if the step was already present; just replace it] -- //
            let foundIndex = tour.steps.reduce(
                (matchedIndex:number, step, index) => step.id === stepId ? index : matchedIndex, -1);

            tour.addStep(stepId, step);
            return tour;
        }
    }

    getStepById(tourId: string, stepId: string) : any {
        let tour = this.findTour(tourId);

        if(!tour) {
            console.warn("No GuidedTour found with Id:", tourId);
            return undefined;
        }

        return tour.getById(stepId);
    }

    /**
     * Must be invoked only after the jquery ('id') elements of the component
     * have been rendered completely.
     *
     * Starts the tour. Idempotent version.
     * @param tourId
     */
    startTour(tourId: string) {
        let tour = this.findTour(tourId);

        if(!tour) {
            console.debug("No GuidedTour found with Id:", tourId);
        }
        else {
            let tourAlreadyStarted = this.getActiveTour();
            if(tourAlreadyStarted && tour) {
                console.debug('Tour already started: ', tourAlreadyStarted);
                return tourAlreadyStarted;
            }

            if(!tour.steps || tour.steps.length<=0)
            {
                /**
                 * Skip if there are no steps defined. This will prevent
                 * spurious tour-events (e.g. 'start' and 'complete')
                 */
                console.debug('Tour not started, as no steps defined: ', tour);
                return tour;
            }

            if(tour.steps[0].options.text.indexOf('guidedTour.') != -1) {
                console.warn('Tour not ready for I18N, skipping... Tour: ', tour);
                return tour;
            }

            tour.start();
            return tour;
        }
    }

    getActiveTour() : any {
        return Shepherd.activeTour;
    }


    exitTour(tourId: string) {
        this.findTour(tourId).cancel();
    }

    exitAllTours() {
        this.tours.forEach ( (entry) => { entry.tour.cancel() })
    }

    destroyAllTours() {
        this.tours.splice(0, this.tours.length);
    }

    removeStep(tourId, stepId) {
        let tour = this.findTour(tourId);

        if(!tour) {
            console.debug("[RemoveStep] No GuidedTour found with Id:", tourId);
        }
        else {
            tour.removeStep(stepId);
        }
    }

    /**
     * Includes (effectively) a call to this.exitAllTours()
     * @param tourId
     */
    removeAllSteps(tourId) {
        let tour = this.findTour(tourId);

        if(!tour) {
            console.debug("[RemoveAllSteps] No Guided Tour found with Id:", tourId);
        }
        else {
            tour.cancel();
            tour.steps = [];
            console.debug("Cleaned up All Steps");
        }
    }

    removeStepsForAllTours() {
        this.tours.forEach ( (entry) => {
            entry.tour.cancel();
            entry.tour.steps = [];
            console.debug("Cleaned up All Steps for the tour:", entry.tour);
        })
    }


    /**
     * Looks for the texts containing '.guidedTour.' and replaces
     * those with I18N translated values.
     *
     * @param tourId
     * @param txService
     */
    resolveExternalizedTexts(tourId, txService: TranslateService) {
        let tour = this.findTour(tourId);

        if(!tour) {
            console.debug("No Guided Tour found with Id:", tourId);
        }
        else {
            if(!tour.steps || tour.steps.length <=0) {
                return;
            }

            let tempTour = new Shepherd.Tour();
            tour.steps.forEach((step) => {
                step.options.text = this.fixI18N(step.options.text, txService);
                step.options.title = this.fixI18N(step.options.title, txService);
                tempTour.addStep(step);
            });

            tour.steps = [];
            tempTour.steps.forEach((step) => {
                tour.addStep(step.id, step);
            });
        }
    }

    private fixI18N(key: string, txService: TranslateService):string {

        if(key && key.indexOf('.guidedTour.') != -1)
        {
            return txService.instant(key);
        }

        return key;
    }
}


// WEBPACK FOOTER //
// ./src/app/services/guided-tour-builder/guided-tour-builder.service.ts